import {
  type ComponentType,
  type HTMLAttributes,
  type Ref,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useHighlight } from "react-css-highlight";
import { List, type ListImperativeAPI } from "react-window";
import { debounce } from "es-toolkit/function";
import { LISTBOX_PADDING } from "../../PitSelector.constants";
import type {
  OptionItemData,
  PitSelectorDefaultRenderOptionProps,
  PitSelectorDefaultRenderOptionSlotProps,
  PitSelectorSlots,
} from "../../PitSelector.types";
import PitSelectorRowComponent from "./components/PitSelectorRowComponent/PitSelectorRowComponent";
import { useScrollToSelectedOption } from "./PitSelectorListbox.hooks";

type ItemData = OptionItemData[];

type PitSelectorListboxProps = HTMLAttributes<HTMLElement> & {
  ref: Ref<HTMLDivElement>;
  internalListRef: React.RefObject<ListImperativeAPI>;
  onItemsBuilt: (optionIndexMap: Map<string, number>) => void;
  RenderOption: ComponentType<PitSelectorDefaultRenderOptionProps>;
  getRowHeight: (idx: number, { itemData }: { itemData: ItemData }) => number;
  getTotalHeight: (itemCount: number) => number;
  highlightText?: string;
  slotProps?: PitSelectorDefaultRenderOptionSlotProps;
  slots?: PitSelectorSlots;
};

const PitSelectorListbox = ({
  ref,
  children,
  internalListRef,
  onItemsBuilt,
  RenderOption,
  getRowHeight,
  getTotalHeight,
  slotProps,
  slots,
  highlightText,
  ...other
}: PitSelectorListboxProps) => {
  const itemData = children as ItemData;
  const itemCount = itemData.length;

  const highlightTextRefElem = useRef<HTMLElement | null>(null);

  highlightTextRefElem.current = internalListRef?.current?.element as HTMLElement;

  const { refresh } = useHighlight({
    debounce: 0,
    search: highlightText || "",
    targetRef: highlightTextRefElem,
  });

  // Build map of uid -> index for keyboard navigation scrolling
  useEffect(() => {
    const optionIndexMap = new Map<string, number>();
    itemData.forEach(([, option], index) => {
      optionIndexMap.set(String(option.uid), index);
    });
    onItemsBuilt?.(optionIndexMap);
  }, [itemData, onItemsBuilt]);

  const scrollToSelected = useScrollToSelectedOption(itemData, internalListRef);

  const debouncedRefresh = useMemo(() => debounce(refresh, 100), [refresh]);

  useEffect(
    () => () => {
      debouncedRefresh.cancel();
    },
    [debouncedRefresh]
  );

  // Separate className for List, other props for wrapper div (ARIA, handlers)
  const { className, style, ...otherProps } = other;

  return (
    <div ref={ref} {...otherProps}>
      <List
        className={className}
        listRef={internalListRef}
        key={itemCount}
        rowCount={itemCount}
        rowHeight={getRowHeight}
        rowComponent={PitSelectorRowComponent}
        rowProps={{
          itemData,
          RenderOption,
          onClick: slotProps?.renderOption?.onClick,
          slots,
          slotProps,
        }}
        style={{
          height: getTotalHeight(itemCount) + 2 * LISTBOX_PADDING,
          maxHeight: "fit-content",
          width: "100%",
        }}
        overscanCount={5}
        tagName="ul"
        onRowsRendered={() => {
          scrollToSelected();
          debouncedRefresh();
        }}
      />
    </div>
  );
};

export default PitSelectorListbox;
